/**
 * CareLogix™ ORACLE — Wearable Intelligence™ Hub.
 *
 * Truthfulness mandate:
 *  - No fabricated samples — ORACLE only displays measurements returned by
 *    the provider's real API.
 *  - Pending integrations are surfaced to members as "Coming Soon".
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  "";

async function _get(p) {
  try { const r = await fetch(`${BACKEND}${p}`); if (!r.ok) return null; return await r.json(); }
  catch { return null; }
}
async function _post(p, body) {
  try {
    const r = await fetch(`${BACKEND}${p}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return await r.json();
  } catch (e) { return { ok: false, error: String(e) }; }
}

const S = {
  page: { minHeight: "100vh", padding: "32px 40px 80px", background: "linear-gradient(160deg, #050b09 0%, #06231b 60%, #050b09 100%)", color: "#dfe3eb", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 22 },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", marginBottom: 4 },
  title: { color: "#fde68a", margin: 0, fontSize: 28, fontFamily: "'Cinzel', serif", letterSpacing: "0.02em" },
  sub: { color: "#cbd5e1", marginTop: 8, fontSize: 13, maxWidth: 760, lineHeight: 1.5 },
  link: { color: "#c5a059", textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.1em", marginRight: 18 },
  section: { background: "rgba(13,26,15,0.65)", border: "1px solid rgba(245,200,66,0.20)", borderRadius: 14, padding: 18, marginBottom: 20 },
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18, margin: 0, marginBottom: 4 },
  sectionSub: { color: "#cbd5e1", fontSize: 12, lineHeight: 1.5, marginBottom: 12, marginTop: 4 },
  card: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 12, padding: 16 },
  cardTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 16, marginBottom: 6 },
  pill: { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "3px 10px", border: "1px solid", borderRadius: 999 },
  metrics: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 },
  metric: { padding: "5px 10px", border: "1px solid rgba(245,200,66,0.35)", borderRadius: 999, background: "rgba(245,200,66,0.08)", color: "#fde68a", fontFamily: "'Space Mono', monospace", fontSize: 10 },
  btn: { appearance: "none", background: "transparent", border: "1px solid rgba(245,200,66,0.50)", color: "#fde68a", padding: "7px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", marginRight: 6, marginTop: 8 },
  input: { background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "8px 11px", borderRadius: 8, fontSize: 12, minWidth: 240, marginRight: 8 },
  compliance: { marginTop: 28, padding: "14px 18px", background: "rgba(252,211,77,0.06)", border: "1px solid rgba(252,211,77,0.30)", borderRadius: 12, color: "#fde68a", fontSize: 12, lineHeight: 1.5 },
};

function Pill({ children, color }) {
  return <span style={{ ...S.pill, color, borderColor: color, background: `${color}1a` }}>{children}</span>;
}

export default function OracleWearablesPage() {
  const [manifest, setManifest] = useState(null);
  const [status, setStatus]     = useState(null);
  const [userId, setUserId]     = useState("");
  const [busy, setBusy]         = useState("");
  const [results, setResults]   = useState({});
  const _once = useRef(false);

  useEffect(() => {
    if (_once.current) return; _once.current = true;
    (async () => {
      const [m, s] = await Promise.all([
        _get("/api/oracle/wearables/manifest"),
        _get("/api/oracle/wearables/status?tier=executive"),
      ]);
      setManifest(m); setStatus(s);
    })();
    try { const saved = window.localStorage.getItem("carelogix_wearables_user"); if (saved) setUserId(saved); } catch (_e) { /* ignore */ }
  }, []);

  const handleConnect = useCallback(async (provider) => {
    if (!userId) { window.alert("Enter a member identifier first."); return; }
    try { window.localStorage.setItem("carelogix_wearables_user", userId); } catch (_e) { /* ignore */ }
    setBusy(`connect:${provider}`);
    const out = await _post(`/api/oracle/wearables/connect/${encodeURIComponent(provider)}`, { user_id: userId });
    setBusy("");
    setResults((r) => ({ ...r, [provider]: out }));
  }, [userId]);

  const handleSync = useCallback(async (provider) => {
    setBusy(`sync:${provider}`);
    const out = await _post(`/api/oracle/wearables/sync/${encodeURIComponent(provider)}`, {});
    setBusy("");
    setResults((r) => ({ ...r, [provider]: out }));
  }, []);

  const providersFromStatus = (status && status.providers) || [];
  const providersFromManifest = (manifest && manifest.providers) || [];
  const providers = providersFromStatus.length > 0 ? providersFromStatus : providersFromManifest;
  const metrics = (manifest && manifest.metrics) || [];

  return (
    <main style={S.page} data-testid="oracle-wearables-page">
      <header style={S.header}>
        <div>
          <div style={S.eyebrow}>ORACLE LIFECOMMAND™ · WEARABLE INTELLIGENCE™</div>
          <h1 style={S.title}>Wearables — connect once, ORACLE handles the rest</h1>
          <p style={S.sub}>
            Connect Oura, Fitbit, Whoop, Apple Health and more. ORACLE only displays measurements
            returned by the provider&apos;s real API — no fabricated samples, ever. Connectors marked
            <strong> Coming Soon</strong> will activate as integrations come online.
            <strong> Decision-support only. No diagnosis. No treatment.</strong>
          </p>
        </div>
        <div>
          <Link to="/oracle/lifescore" style={S.link} data-testid="link-lifescore">← LifeScore™</Link>
          <Link to="/oracle/animations" style={S.link} data-testid="link-animations">Animation Library →</Link>
          <Link to="/oracle/lifecommand" style={S.link} data-testid="link-lifecommand">LifeCommand Console →</Link>
        </div>
      </header>

      <section style={S.section} data-testid="wearables-user">
        <h2 style={S.sectionTitle}>Your wearable session</h2>
        <p style={S.sectionSub}>Optional member identifier — required only when connecting a per-user token. No PHI required.</p>
        <input data-testid="wearables-user-input" style={S.input} placeholder="Your member email or ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
      </section>

      {metrics.length > 0 && (
        <section style={S.section} data-testid="wearables-metrics">
          <h2 style={S.sectionTitle}>Metrics catalogued</h2>
          <p style={S.sectionSub}>ORACLE normalizes the following dimensions when a wearable graduates to LIVE.</p>
          <div style={S.metrics}>
            {metrics.map((m) => <span key={m} style={S.metric} data-testid={`metric-${m.toLowerCase().replace(/\s+/g, '-')}`}>{m}</span>)}
          </div>
        </section>
      )}

      <section style={S.section} data-testid="wearables-providers">
        <h2 style={S.sectionTitle}>Connectors ({providers.length})</h2>
        <p style={S.sectionSub}>Each provider lists its current availability. Connect / Sync attempts honestly report when an integration is still pending.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
          {providers.map((p) => {
            const key = (p.name || "").toLowerCase().replace(/\s+/g, "_");
            const st  = p.status || "";
            const live = st === "LIVE" || p.live === true;
            const friendlyStatus = live ? "LIVE" : "Coming Soon";
            const res  = results[key];
            return (
              <div key={p.name} style={S.card} data-testid={`wearable-${key}`}>
                <div style={S.cardTitle}>{p.name}</div>
                <Pill color={live ? "#7AC860" : "#fcd34d"}>{friendlyStatus}</Pill>
                <div>
                  <button type="button" style={S.btn} disabled={busy === `connect:${p.name}`} onClick={() => handleConnect(p.name)} data-testid={`wearable-connect-${key}`}>
                    {busy === `connect:${p.name}` ? "Connecting…" : "Connect"}
                  </button>
                  <button type="button" style={S.btn} disabled={busy === `sync:${p.name}`} onClick={() => handleSync(p.name)} data-testid={`wearable-sync-${key}`}>
                    {busy === `sync:${p.name}` ? "Syncing…" : "Sync now"}
                  </button>
                </div>
                {res && (
                  <p style={{ color: "#94a3b8", fontSize: 11, marginTop: 8, fontFamily: "'Space Mono', monospace" }} data-testid={`wearable-result-${key}`}>
                    {res.status || res.synced === false ? `Status: ${(res.status === "LIVE" ? "Live" : "Coming Soon")}` : "—"}
                    {res.next_action ? ` · We’ll notify members when ${p.name} becomes available.` : ""}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div style={S.compliance} data-testid="wearables-compliance">
        {(manifest && manifest.disclaimer) || "Decision-support only. Not medical advice. Wearable data is informational."}
      </div>
    </main>
  );
}
